import { RfqTemplateType } from '@lune-fe/dapi-models'
import {
    BannerMessage,
    DragAndDropFileUpload,
    LoadingWrapper,
    LuneThemeNew,
    Text,
} from '@lune-fe/lune-ui-lib'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import { Box, Link, Stack } from '@mui/material'
import { Workbook } from 'exceljs'
import { useSnackbar } from 'notistack'
import React, { FC, useEffect, useRef, useState } from 'react'
import { FileRejection } from 'react-dropzone'

import { notify } from 'bugsnag'
import { updateRfqTemplate, uploadDataSheet } from 'endpoints/dapi'
import useDataSheets from 'hooks/useDataSheets'
import useMappings from 'hooks/useMappings'
import { assertType } from 'utils'
import ClientAccountMappingFields, {
    ClientAccountMappingResult,
    SheetData,
} from 'views/CalculateEmissions/RFQ/components/ClientAccountMappingFields'
import { StageContext } from 'views/CalculateEmissions/RFQ/components/MultiStepFlow'
import SharedLayout from 'views/CalculateEmissions/RFQ/components/SharedLayout'
import FileSettings from 'views/CalculateEmissions/RFQ/modals/FileSettings'
import InfoMessage from 'views/CalculateEmissions/RFQ/modals/InfoMessage'
import UnmappedProperties from 'views/CalculateEmissions/RFQ/modals/UnmappedProperties'
import {
    generateUnknownErrorInfo,
    generateWorkingData,
    getMappingDataSheet,
    getMappingInfo,
    Info,
    uploadRfqFile,
    validateCsv,
    validateWorkbook,
} from 'views/CalculateEmissions/RFQ/utils/operationUtils'
import { RfqTemplateData, RowNumber } from 'views/CalculateEmissions/RFQ/utils/sharedLogic'
import { MissingMappingData, TypedDapiRfqTemplate } from 'views/CalculateEmissions/RFQ/utils/types'
import { getTableDimensions } from 'views/CalculateEmissions/RFQ/utils/utils'

import {
    handleSheetUploadError,
    MAX_SIZE_BYTES,
    MAX_SIZE_HUMAN_READABLE,
} from '../Shared/CsvSheetCommon'

import NewCalculation, {
    DataSheetJobSettings,
    NewCalculationInformation,
} from './modals/NewCalculation'

type SmartUpdateInfo = {
    unmappedProperties: MissingMappingData[]
    mapping: TypedDapiRfqTemplate
    jobSettings: DataSheetJobSettings
    uploadDataSheet: () => Promise<{ result: 'error' | 'success'; info: Info }>
}

export type FileDetails =
    | { sheetType: 'rfq'; filename: string; data: Workbook }
    | { sheetType: 'logistics_user_template'; filename: string; data: File }

/**
 * Self-contained view for editing client account mappings in the smart update flow.
 * Loads the sheet data from the file and renders the mapping form.
 */
const ClientAccountMappingView: FC<{
    fileDetails: FileDetails
    template: RfqTemplateData
    onSubmit: (result: ClientAccountMappingResult) => void
    onBack: () => void
}> = ({ fileDetails, template, onSubmit, onBack }) => {
    const [sheetData, setSheetData] = useState<SheetData | undefined>()
    const formRef = useRef<HTMLFormElement>(null)

    useEffect(() => {
        const loadSheet = async () => {
            const headerRow = RowNumber.fromOneBased(template.headerRow)
            const sheet = await getMappingDataSheet(fileDetails, template.sheetName, headerRow)
            if (!sheet) return
            const { headerRowNumber, nrColumns, headerStartIndex, endRow } = getTableDimensions(
                sheet,
                headerRow,
            )
            const workingData = generateWorkingData(sheet, headerRowNumber, nrColumns)
            setSheetData({
                sheet,
                headers: workingData.headers,
                exampleRow: workingData.exampleRow,
                headerStartIndex,
                headerRow: template.headerRow,
                endRow: endRow.toZeroBased(),
            })
        }
        loadSheet()
    }, [])

    return (
        <SharedLayout
            loading={!sheetData}
            handleBack={onBack}
            stepsComponent={<Text variant={'h4'}>Client accounts</Text>}
            handleNext={() => formRef.current?.requestSubmit()}
            nextButtonLabel={'Calculate emissions'}
        >
            {sheetData && (
                <ClientAccountMappingFields
                    sheetData={sheetData}
                    onSubmit={onSubmit}
                    formRef={formRef}
                />
            )}
        </SharedLayout>
    )
}

// eslint-disable-next-line complexity
const RFQ = ({
    sheetType,
    context,
    onClose,
    onModalSizeChange,
}: {
    sheetType: RfqTemplateType
    context: StageContext
    onClose: (isSuccess: boolean) => void
    onModalSizeChange?: (size: 'sm' | 'md') => void
}) => {
    if (sheetType !== 'rfq' && sheetType !== 'logistics_user_template') {
        return null
    }

    const { title, subTitle, fileLabel, acceptedFiles } = getMappingInfo(sheetType)
    const { spacing } = LuneThemeNew
    const { refetch } = useDataSheets(sheetType)
    const [isUploading, setUploading] = useState(false)
    const [message, setMessage] = useState<Info | undefined>(undefined)
    const [isUploadSuccess, setIsUploadSuccess] = useState(false)
    const [fileSettingsModalOpen, setFileSettingsModalOpen] = useState<boolean>(false)
    const [newCalculationModalOpen, setNewCalculationModalOpen] = useState<boolean>(false)
    const [missingMappingsModalOpen, setMissingMappingsModalOpen] = useState<
        false | SmartUpdateInfo
    >(false)
    const [clientAccountDetailsOpen, setClientAccountDetailsOpen] = useState<
        false | SmartUpdateInfo
    >(false)
    const [fileInfo, setFileInfo] = useState<FileDetails | undefined>(undefined)
    const ref = React.useRef<HTMLDivElement>(null)

    const { loading: isLoadingMappings, mappings } = useMappings(sheetType)
    const { enqueueSnackbar: snackbar } = useSnackbar()

    // The client accounts details screen needs a bit more space to be able to
    // be understandable and interacted cleanly, up the size in that case.
    useEffect(() => {
        onModalSizeChange?.(clientAccountDetailsOpen ? 'md' : 'sm')
    }, [clientAccountDetailsOpen])

    const resetWorkState = () => {
        setUploading(false)
        setFileSettingsModalOpen(false)
        setNewCalculationModalOpen(false)
        setMissingMappingsModalOpen(false)
        setClientAccountDetailsOpen(false)
        setFileInfo(undefined)
    }

    const handleOperationError = async (info?: Info) => {
        resetWorkState()
        setMessage(info ?? generateUnknownErrorInfo())
    }

    const onUploadSheet = async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
        setUploading(true)
        if (rejectedFiles.length) {
            handleOperationError({
                subject: [
                    `Upload failed', '${fileLabel} files only, up to ${MAX_SIZE_HUMAN_READABLE}`,
                ],
            })

            return
        }
        const filename = acceptedFiles[0].name

        const invalidFileError = (error: Error) => {
            handleOperationError({
                subject: [`We couldn’t open your ${fileLabel} file`],
                description:
                    'Something went wrong while reading the file. It may be corrupted or in an unsupported format.\n\nPlease contact support or try uploading a different version.',
            })
            notify(error)
        }
        if (sheetType === 'logistics_user_template') {
            const csv = await validateCsv(acceptedFiles[0])
            if (csv.isErr()) {
                invalidFileError(csv.error)
                return
            }

            const file = acceptedFiles[0]
            if (csv.value.type === 'legacy_processor') {
                const formData = new FormData()
                formData.append('sheet', file)
                uploadDataSheet(formData, 'logistics')
                    .then(() => {
                        setIsUploadSuccess(true)
                        // Reuse the error modal to give the success message
                        setMessage({
                            subject: [
                                `Upload complete.`,
                                `Processing your file, we'll email you when ready.`,
                            ],
                            description: 'Should take a few minutes',
                        })
                        refetch()
                        setUploading(false)
                    })
                    .catch((err) => {
                        setIsUploadSuccess(false)
                        handleOperationError(handleSheetUploadError(err))
                    })
                return
            }

            context.updateState({
                workbook: file,
                sheetType,
                workbookBase64: csv.value.base64Data,
            })

            setFileInfo({ sheetType, filename, data: file })
            setUploading(false)
            setNewCalculationModalOpen(true)
            return
        }

        assertType<'rfq'>(sheetType)
        // TODO upload file, maybe infer next properties immediatelly (sheet to use, header row etc.)
        acceptedFiles[0]
            .arrayBuffer()
            .then(async (arrayBuffer) => {
                const workbook = await validateWorkbook(arrayBuffer)
                if (workbook.isErr()) {
                    invalidFileError(workbook.error.info)
                    return
                }
                context.updateState({
                    workbook: workbook.value.workbook,
                    sheetType,
                    workbookBase64: workbook.value.base64Data,
                })
                setFileInfo({ sheetType, filename, data: workbook.value.workbook })
                setUploading(false)
                if (isLoadingMappings || mappings.length) {
                    setNewCalculationModalOpen(true)
                } else {
                    setFileSettingsModalOpen(true)
                }
            })
            .catch((_e) => {
                handleOperationError()
            })
    }

    const uploadContent = (
        <>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 3,
                }}
            >
                <Text variant={'h5'}>{title}</Text>
                <Text variant={'body2'} sx={{ color: `Grey700`, display: 'block' }}>
                    {subTitle}
                </Text>
            </Box>
            <Box sx={{ height: spacing(25), width: '100%' }}>
                <LoadingWrapper loading={isUploading} sx={{ width: '100%', height: '100%' }}>
                    <DragAndDropFileUpload
                        /*
                         * Property 'ref' does not exist on type 'IntrinsicAttributes & { children?: ReactNode; isUploading?: boolean | undefined; } & Pick<HTMLProps<HTMLElement>, PropTypes> & { ...; }'.
                         */
                        ref={ref}
                        accept={acceptedFiles}
                        maxSize={MAX_SIZE_BYTES}
                        isUploading={isUploading}
                        onDrop={onUploadSheet}
                        rejectDescription={`Excel files only, up to ${MAX_SIZE_HUMAN_READABLE}`}
                        description={
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexDirection: 'column',
                                    gap: 1,
                                }}
                            >
                                <FileUploadOutlinedIcon
                                    sx={{
                                        width: '32px',
                                        height: '32px',
                                    }}
                                />
                                <Text variant={'body2'}>
                                    Drag and drop your {fileLabel} here or{' '}
                                    <Link
                                        onClick={() => {
                                            if (ref.current) {
                                                ref.current.click()
                                            }
                                        }}
                                        color={'TextPrimary'}
                                        fontWeight={'medium'}
                                    >
                                        browse files
                                    </Link>
                                </Text>
                                <Text variant={'body3'} color={'TextTertiary'}>
                                    {sheetType === 'rfq' ? '.xls' : '.csv'} up to{' '}
                                    {MAX_SIZE_HUMAN_READABLE}
                                </Text>
                            </Box>
                        }
                    />
                </LoadingWrapper>
            </Box>
            <BannerMessage
                message={`Calculations will count towards your tier limit, one per row`}
            />
        </>
    )

    const fileSettingsContent = (
        <FileSettings
            sheetType={sheetType}
            // onModalClose={resetWorkState}
            context={context}
        />
    )

    const newCalculationContent = fileInfo ? (
        <NewCalculation
            fileInfo={fileInfo}
            afterSubmit={async (result: NewCalculationInformation | undefined) => {
                if (result) {
                    if (result.newOrExisting === 'new') {
                        if (!context.state) {
                            snackbar(`File could not be found.`)
                            return
                        }

                        context.updateState({
                            ...context.state,
                            jobSettings: result.jobSettings,
                        })
                        setFileSettingsModalOpen(true)
                    } else {
                        assertType<'existing'>(result.newOrExisting)
                        const uploadDataSheet = async () => {
                            const uploadOperation = await uploadRfqFile(
                                fileInfo.sheetType === 'rfq'
                                    ? { workbook: fileInfo.data }
                                    : { csvFile: fileInfo.data },
                                {
                                    mappingId: result.selectedExistingMapping.id,
                                    jobSettings: result.jobSettings,
                                },
                            )
                            if (uploadOperation.result === 'success') {
                                setIsUploadSuccess(true)
                                refetch()
                            } else {
                                setIsUploadSuccess(false)
                            }
                            setMissingMappingsModalOpen(false)
                            // Reuse the error modal to give the success message
                            setMessage(uploadOperation.info)
                            // Also return the result so UnmappedPropertiesModal can handle redirect
                            return uploadOperation
                        }
                        // Client account details come first, then unmapped properties
                        if (result.missingClientAccountInformation) {
                            handleTransitionToClientAccountDetails({
                                mapping: result.selectedExistingMapping,
                                jobSettings: result.jobSettings,
                                unmappedProperties: result.unmappedProperties,
                                uploadDataSheet,
                            })
                            setNewCalculationModalOpen(false)
                            return
                        }

                        if (result.unmappedProperties.length) {
                            setMissingMappingsModalOpen({
                                unmappedProperties: result.unmappedProperties,
                                mapping: result.selectedExistingMapping,
                                jobSettings: result.jobSettings,
                                uploadDataSheet,
                            })
                            setNewCalculationModalOpen(false)
                            return
                        }

                        const uploadOperation = await uploadRfqFile(
                            fileInfo.sheetType === 'rfq'
                                ? { workbook: fileInfo.data }
                                : { csvFile: fileInfo.data },
                            {
                                mappingId: result.selectedExistingMapping.id,
                                jobSettings: result.jobSettings,
                            },
                        )
                        if (uploadOperation.result === 'success') {
                            setIsUploadSuccess(true)
                            refetch()
                        } else {
                            setIsUploadSuccess(false)
                        }
                        // Reuse the error modal to give the success message
                        setMessage(uploadOperation.info)
                    }
                    setNewCalculationModalOpen(false)
                } else {
                    // on Cancel button click
                    resetWorkState()
                }
            }}
        />
    ) : null

    const messageContent = message && (
        <InfoMessage
            subject={message.subject}
            description={message.description}
            onClose={() => {
                resetWorkState()
                setMessage(undefined)
                onClose(isUploadSuccess)
            }}
        />
    )

    const missingMappingsContent =
        missingMappingsModalOpen && fileInfo ? (
            <UnmappedProperties
                unmappedProperties={missingMappingsModalOpen.unmappedProperties}
                mapping={missingMappingsModalOpen.mapping}
                fileDetails={fileInfo}
                uploadDataSheet={missingMappingsModalOpen.uploadDataSheet}
                onClose={resetWorkState}
            />
        ) : null

    const handleTransitionToClientAccountDetails = (details: SmartUpdateInfo) => {
        setClientAccountDetailsOpen(details)
    }

    const handleClientAccountSubmit = async (
        details: SmartUpdateInfo,
        result: ClientAccountMappingResult,
    ) => {
        const updatedTemplate = {
            ...details.mapping.template,
            clientAccountName: result.clientAccountName,
            clientAccountCurrency: result.clientAccountCurrency,
        }
        setClientAccountDetailsOpen(false)

        // Either show smart unmapped values modal or just upload and finish job.
        if (details.unmappedProperties.length) {
            setMissingMappingsModalOpen({
                ...details,
                mapping: { ...details.mapping, template: updatedTemplate },
            })
        } else {
            await updateRfqTemplate(details.mapping.id, details.mapping.name, updatedTemplate)
            const uploadResult = await details.uploadDataSheet()
            if (uploadResult.result === 'success') {
                setIsUploadSuccess(true)
                refetch()
            } else {
                setIsUploadSuccess(false)
            }
            setMessage(uploadResult.info)
        }
    }

    return clientAccountDetailsOpen && fileInfo ? (
        <ClientAccountMappingView
            fileDetails={fileInfo}
            template={clientAccountDetailsOpen.mapping.template}
            onSubmit={(result) => handleClientAccountSubmit(clientAccountDetailsOpen, result)}
            onBack={() => onClose(false)}
        />
    ) : (
        <>
            <Stack spacing={{ xs: 4, width: '100%' }}>
                {fileInfo && fileSettingsModalOpen && fileSettingsContent}
                {fileInfo && newCalculationModalOpen && newCalculationContent}
                {message && messageContent}
                {missingMappingsModalOpen && fileInfo && missingMappingsContent}
                {!fileInfo && !message && uploadContent}
            </Stack>
        </>
    )
}

export default RFQ
